import { FC, useEffect } from "react";
import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDialog,
  FormDialogCancel,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Separator,
  Spinner,
} from "@capacit-com/lara-law-shared-ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { Globe, Link2, Pencil, PlusIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { WorkspaceLinkRequest } from "@/models/workspace/Workspace";
import { Item } from "@/pages/workspace/utils/workspaceItemUtils";
import {
  linkFormSchema,
  LinkFormValues,
  normalizeLinkUrl,
} from "@/pages/workspace/utils/linkUrl";

/** Trimmed name and absolute http(s) address — exactly what the API accepts. */
export type LinkDialogValues = WorkspaceLinkRequest;

export type LinkDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  /** The link being edited, or null when adding a new one. */
  item: Item | null;
  /**
   * Called with the trimmed name and the normalized address. Only invoked when
   * something actually changed. Reject to keep the dialog open with the values intact.
   */
  onSubmit: (values: LinkDialogValues) => Promise<void>;
  isPending: boolean;
};

/**
 * One dialog for both "Tilføj link" and "Rediger link" (LAR-415): the same two
 * fields, the same rules, only the copy and the submit label differ. It owns no
 * mutation — the caller does — so it can be tested with plain props.
 */
const LinkDialog: FC<LinkDialogProps> = ({
  isOpen,
  onClose,
  item,
  onSubmit,
  isPending,
}) => {
  const isEdit = item !== null;

  const form = useForm<LinkFormValues>({
    resolver: zodResolver(linkFormSchema),
    defaultValues: { name: item?.name ?? "", url: item?.url ?? "" },
  });

  // The dialog stays mounted between uses, so re-seed the fields whenever it opens
  // for a different link (or for a fresh add) — otherwise the previous values stick.
  useEffect(() => {
    if (isOpen) form.reset({ name: item?.name ?? "", url: item?.url ?? "" });
  }, [isOpen, item?.id, item?.name, item?.url, form]);

  const handleSubmit = form.handleSubmit(async ({ name, url }) => {
    const normalizedUrl = normalizeLinkUrl(url);
    // The schema already refused anything that does not normalize; this only narrows the type.
    if (!normalizedUrl) return;

    const values: LinkDialogValues = { name: name.trim(), url: normalizedUrl };
    if (item && values.name === item.name && values.url === item.url) {
      onClose();
      return;
    }

    try {
      await onSubmit(values);
      onClose();
    } catch {
      // The caller reports the failure; keep the dialog open so nothing typed is lost.
    }
  });

  return (
    <FormDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isPending) onClose();
      }}
      isDirty={form.formState.isDirty}
      size="md"
    >
      <DialogHeader className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="bg-primary/10 p-2 rounded-full">
            <Link2 className="h-5 w-5 text-primary" />
          </div>
          <DialogTitle className="text-xl">
            {isEdit ? "Rediger link" : "Tilføj link"}
          </DialogTitle>
        </div>
        <DialogDescription className="text-sm text-muted-foreground">
          {isEdit
            ? "Ret navnet eller webadressen. Linket bliver ikke flyttet."
            : "Gem en webadresse, så den vises sammen med workspacets dokumenter og mapper."}
        </DialogDescription>
      </DialogHeader>

      <Separator className="my-1" />

      <Form {...form}>
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1.5">
                  <Pencil className="h-3.5 w-3.5 text-primary" />
                  Navn
                </FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="F.eks. Retsinformation"
                      className="pl-9"
                      autoFocus
                      data-testid="link-name"
                    />
                  </FormControl>
                  <Link2 className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="url"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1.5">
                  <Globe className="h-3.5 w-3.5 text-primary" />
                  Webadresse
                </FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="https://www.retsinformation.dk"
                      className="pl-9"
                      inputMode="url"
                      autoComplete="off"
                      spellCheck={false}
                      data-testid="link-url"
                    />
                  </FormControl>
                  <Globe className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <DialogFooter className="gap-2 space-x-2 sm:gap-0 mt-6">
            <FormDialogCancel disabled={isPending} />
            <Button
              type="submit"
              className="gap-2 min-w-[100px]"
              disabled={isPending}
              data-testid="link-submit"
            >
              {isPending ? (
                <Spinner className="h-4 w-4 text-primary-foreground" />
              ) : (
                <span className="flex items-center gap-2">
                  {isEdit ? (
                    <Pencil className="h-4 w-4" />
                  ) : (
                    <PlusIcon className="h-4 w-4" />
                  )}
                  {isEdit ? "Gem" : "Tilføj"}
                </span>
              )}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </FormDialog>
  );
};

export default LinkDialog;
